/* ==========================================================================
   Mandira's Kitchen - Free Gift Manager   v3.4
   --------------------------------------------------------------------------
   MASTER SWITCH
     There is ONE place to turn the free-gift system on or off:

         templates/cart.reward-data.liquid  ->  assign reward_active = false

     This file does not need editing to switch the reward off. It reads
     reward_active from the JSON endpoint on every check. When it is false the
     script never adds a gift, forces the styling hook to
     data-mk-gift-earned="false", and reclaims any gift it had previously put
     into a cart (leaving alone any gift product the customer chose to buy). Keep the
     Shopify automatic Buy X Get Y discount deactivated while it is false.

     window.MK_GIFT_CONFIG.enabled is a separate, blunter switch set in
     snippets/mk-reward-guard.liquid: it stops this script running at all,
     including the tidy-up. Use reward_active for normal on/off. Use enabled
     only if you want the script inert.

   --------------------------------------------------------------------------
   ARCHITECTURE
     Liquid decides      templates/cart.reward-data.liquid computes the
                         eligible subtotal from product.collections and serves
                         it as JSON at /cart?view=reward-data.
     This file adds      Puts the gift in the cart when earned, takes back only
                         what it added, and tidies the drawer labels.
     Shopify discounts   AUTOMATIC Buy X Get Y makes ONE gift GBP 0.
     UpCart              Reward tier OFF. Renders the drawer only.

   v3.3 - label tidying rewritten
     v3.2 only matched elements with no child elements, so a pill built as
     <div><svg/>LABEL TEXT</div> was missed - the text sits in a div that does
     have a child. Now it finds the DEEPEST element whose entire text is the
     discount title, whatever the markup around it.

     Row detection is also inverted and more reliable. Instead of hunting for
     each product's row, it collects every label in the drawer and walks up
     from each one until it finds an ancestor holding EXACTLY ONE product link.
     That ancestor is the row, and the link says which product it belongs to.
     If an ancestor holds two or more product links we have escaped the row,
     so the label is left alone - which is what keeps the footer Discounts
     summary intact without special-casing it.

     Labels are kept where the line has a real discount, hidden where the
     allocation is GBP 0.00. Shopify attaches an allocation to every
     PREREQUISITE line, not just the discounted one, which is why qualifying
     food was showing a "FREE ... OVER GBP 75" label worth zero.

     Diagnostics: MKGift.debugLabels() reports what it found and what it did.

   SAFETY NET
     Judged on the line's DISCOUNT, not its price, because at quantity 2 the
     correct price is one unit's price. If the cart qualifies, the gift is present, and
     Shopify allocated no discount for more than a few seconds, the script
     reclaims its own gift. A missing freebie beats an unexpected charge.

   v3.4 - master switch honoured
     reward_active from the Liquid endpoint now gates everything, so the whole
     system is switched from one line in cart.reward-data.liquid.
     MKGift.state.rewardActive reports what the endpoint said.

   v3.5 - monthly gift schedule (26 Sep 2026)
     WHICH product is the gift now comes from the schedule in
     templates/cart.reward-data.liquid (gift_handle / gift_variant_id in the
     JSON). Nothing else changed: same goal, same collections, same add /
     reclaim / safety-net / label rules. The only new behaviour is that a gift
     this script added in an earlier month (line.is_stale_gift) is handed back,
     exactly as a gift is reclaimed when the reward is switched off.
     MK_GIFT_CONFIG.giftVariantId is now only a fallback for an old endpoint.

   Console: MKGift.state / MKGift.data() / MKGift.check() / MKGift.debugLabels()
   ========================================================================== */
(function () {
  'use strict';

  var C = Object.assign(
    {
      endpoint: '/cart?view=reward-data',
      giftVariantId: null,
      enabled: true,
      undiscountedGraceMs: 4000,
      tidyLineDiscountLabels: true,
      hideSelectors: [],
      pollMs: 6000,
      debug: false
    },
    window.MK_GIFT_CONFIG || {}
  );

  var GIFT_PROPERTY = '_mk_gift';

  var lastData = null;
  var writeLog = [];
  var busy = false;
  var timer = null;
  var tidyTimer = null;
  var stoodDown = false;
  var offNoted = false;
  var undiscountedSince = null;

  function log() {
    if (C.debug && window.console) {
      console.log.apply(console, ['[MK-Gift]'].concat([].slice.call(arguments)));
    }
  }

  function warn(msg) {
    if (window.console && console.warn) console.warn('[MK-Gift] ' + msg);
  }

  function money(pence) {
    return '£' + (pence / 100).toFixed(2);
  }

  function sum(list, field) {
    return list.reduce(function (total, item) { return total + (item[field] || 0); }, 0);
  }

  function normalise(s) {
    return String(s || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  /* ---------- eligibility data (computed in Liquid) ---------------------- */

  function getData() {
    return fetch(C.endpoint, { cache: 'no-store', credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('reward-data returned HTTP ' + r.status);
        return r.text();
      })
      .then(function (text) {
        try {
          return JSON.parse(text);
        } catch (e) {
          throw new Error(
            'reward-data is not valid JSON. Is templates/cart.reward-data.liquid present? ' +
              'First 120 chars: ' + text.slice(0, 120)
          );
        }
      });
  }

  /* ---------- drawer label tidying --------------------------------------- */

  function drawerRoots() {
    var found = [];
    ['#UpcartPopup', '.upcart-cart', '[class*="upcart-public"]', '[id*="upcart" i]', '[class*="upcart"]']
      .forEach(function (sel) {
        try {
          var nodes = document.querySelectorAll(sel);
          for (var i = 0; i < nodes.length; i++) {
            if (found.indexOf(nodes[i]) === -1) found.push(nodes[i]);
          }
        } catch (e) {}
      });
    // Keep only outermost matches so we do not scan the same subtree repeatedly.
    return found.filter(function (el) {
      return !found.some(function (other) { return other !== el && other.contains(el); });
    });
  }

  // Every element whose ENTIRE text is one of the titles, keeping only the
  // deepest such element in each nesting chain. Works whether the text sits in
  // its own span or directly alongside an icon.
  function findLabels(scope, titles) {
    var matches = [];
    var all = scope.querySelectorAll('*');
    for (var i = 0; i < all.length; i++) {
      if (titles.indexOf(normalise(all[i].textContent)) !== -1) matches.push(all[i]);
    }
    return matches.filter(function (el) {
      return !matches.some(function (other) { return other !== el && el.contains(other); });
    });
  }

  // Walk outward until we find an ancestor with exactly one product link. That
  // is the row. Two or more links means we have left the row - stop, and treat
  // the label as not belonging to any single product (e.g. the footer summary).
  function rowHandleFor(label, root) {
    var el = label;
    var depth = 0;
    while (el && el !== root && depth < 12) {
      var links = el.querySelectorAll('a[href*="/products/"]');
      if (links.length === 1) {
        var href = links[0].getAttribute('href') || '';
        var m = href.match(/\/products\/([^?#\/]+)/);
        return m ? m[1] : null;
      }
      if (links.length > 1) return null;
      el = el.parentElement;
      depth++;
    }
    return null;
  }

  function tidyLabels(data, verbose) {
    var report = { roots: 0, titles: [], labels: 0, hidden: 0, kept: [] };
    if (!C.tidyLineDiscountLabels || !data) return report;

    var titles = [];
    var keepHandles = [];
    (data.lines || []).forEach(function (l) {
      var t = normalise(l.discount_title);
      if (t && titles.indexOf(t) === -1) titles.push(t);
      if (l.discount > 0 && keepHandles.indexOf(l.handle) === -1) keepHandles.push(l.handle);
    });
    report.titles = titles;
    if (!titles.length) return report;

    var roots = drawerRoots();
    report.roots = roots.length;

    roots.forEach(function (root) {
      findLabels(root, titles).forEach(function (label) {
        report.labels++;
        var handle = rowHandleFor(label, root);

        if (!handle) {                      // footer summary or unattributable
          report.kept.push('(no single product link)');
          return;
        }
        if (keepHandles.indexOf(handle) !== -1) {   // real saving - keep
          report.kept.push(handle);
          return;
        }
        // Idempotent, so this cannot retrigger the MutationObserver.
        if (label.style.display !== 'none') {
          label.style.display = 'none';
          report.hidden++;
          log('hid zero-value discount label on', handle);
        }
      });
    });

    if (verbose && window.console) console.log('[MK-Gift] label report', report);
    return report;
  }

  function watchDrawer() {
    var roots = drawerRoots();
    if (!roots.length) return false;
    roots.forEach(function (root) {
      if (root.getAttribute('data-mk-watched') === 'true') return;
      root.setAttribute('data-mk-watched', 'true');
      try {
        new MutationObserver(function () {
          clearTimeout(tidyTimer);
          tidyTimer = setTimeout(function () { tidyLabels(lastData); }, 80);
        }).observe(root, { childList: true, subtree: true });
        log('watching drawer for re-renders');
      } catch (e) {
        log('could not observe drawer', e);
      }
    });
    return true;
  }

  /* ---------- cart writes ------------------------------------------------ */

  function rateLimited() {
    var now = Date.now();
    var kept = writeLog.filter(function (t) { return now - t < 20000; });
    writeLog.length = 0;
    kept.forEach(function (t) { writeLog.push(t); });

    if (writeLog.length >= 5) {
      if (!stoodDown) {
        stoodDown = true;
        warn('Stood down after 5 cart writes in 20s. Inspect MKGift.state. MKGift.reset() to resume.');
      }
      return true;
    }
    writeLog.push(now);
    return false;
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error(url + ' returned HTTP ' + r.status);
      return r.json();
    });
  }

  function redraw() {
    try {
      if (typeof window.upcartRefreshCart === 'function') {
        window.upcartRefreshCart();
        return;
      }
    } catch (e) {}
    try {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    } catch (e) {}
  }

  // The scheduled gift for this month, from the Liquid endpoint. Falls back to
  // the snippet config only if the endpoint does not supply one.
  function currentGiftVariantId() {
    if (lastData && lastData.gift_variant_id) return Number(lastData.gift_variant_id);
    return C.giftVariantId ? Number(C.giftVariantId) : null;
  }

  function addGift() {
    var variantId = currentGiftVariantId();
    if (!variantId) {
      warn('No gift variant for this month - cannot add the gift.');
      return Promise.resolve();
    }
    if (rateLimited()) return Promise.resolve();

    var properties = {};
    properties[GIFT_PROPERTY] = 'true';

    return post('/cart/add.js', {
      items: [{ id: variantId, quantity: 1, properties: properties }]
    })
      .then(function () {
        log('gift added');
        undiscountedSince = null;
        document.dispatchEvent(new CustomEvent('mk:gift:added'));
        redraw();
      })
      .catch(function (e) { warn('could not add gift: ' + e.message); });
  }

  // Take back exactly one unit from a line this script added. Never touches a
  // gift product the customer put in the cart themselves.
  function reclaimGift(ourGiftLines, reason) {
    if (!ourGiftLines.length) {
      log('cart does not qualify, but the gift items present were added by the customer - leaving them alone');
      return Promise.resolve();
    }
    if (rateLimited()) return Promise.resolve();

    var line = ourGiftLines[0];
    var updates = {};
    updates[line.key] = Math.max(0, line.quantity - 1);

    undiscountedSince = null;

    return post('/cart/update.js', { updates: updates })
      .then(function () {
        log(reason + ' (line set to ' + updates[line.key] + ')');
        document.dispatchEvent(new CustomEvent('mk:gift:removed', { detail: { reason: reason } }));
        redraw();
      })
      .catch(function (e) { warn('could not update cart: ' + e.message); });
  }

  /* ---------- UI hook ---------------------------------------------------- */

  function paint(qualifies) {
    document.documentElement.setAttribute('data-mk-gift-earned', qualifies ? 'true' : 'false');
    (C.hideSelectors || []).forEach(function (sel) {
      var nodes = document.querySelectorAll(sel);
      for (var i = 0; i < nodes.length; i++) {
        nodes[i].style.display = qualifies ? '' : 'none';
      }
    });
  }

  /* ---------- main ------------------------------------------------------- */

  function check() {
    if (busy || !C.enabled) return Promise.resolve();
    busy = true;

    return getData()
      .then(function (data) {
        lastData = data;

        var giftLines = [];
        var ourGiftLines = [];
        var counting = [];
        var ignored = [];
        var staleGiftLines = [];

        (data.lines || []).forEach(function (line) {
          if (line.is_stale_gift) {
            staleGiftLines.push(line);   // last month's gift, added by this script
          } else if (line.is_reward) {
            giftLines.push(line);
            if (line.is_our_gift) ourGiftLines.push(line);
          } else if (line.counts) {
            counting.push(line);
          } else {
            ignored.push(line);
          }
        });

        // The endpoint is the authority. If it is an older copy of
        // cart.reward-data.liquid with no reward_active field, treat the reward
        // as active so nothing changes for it.
        var rewardActive = data.reward_active !== false;
        var qualifies = rewardActive && data.qualifies === true;
        var giftQty = sum(giftLines, 'quantity');
        var giftDiscount = sum(giftLines, 'discount');

        window.MKGift.state = {
          rewardActive: rewardActive,
          giftHandle: data.gift_handle || null,
          giftVariantId: currentGiftVariantId(),
          staleGifts: sum(staleGiftLines, 'quantity'),
          qualifies: qualifies,
          eligibleSubtotal: money(data.eligible_subtotal),
          goal: money(data.goal),
          shortBy: money(Math.max(0, data.goal - data.eligible_subtotal)),
          rawCartTotal: money(data.cart_total),
          giftQuantity: giftQty,
          giftDiscountApplied: giftQty ? money(giftDiscount) : null,
          discountWorking: giftQty ? giftDiscount > 0 : null,
          addedByScript: sum(ourGiftLines, 'quantity'),
          counting: counting.map(function (l) { return l.title + ' ' + money(l.line_price); }),
          ignored: ignored.map(function (l) { return l.title + ' ' + money(l.line_price); }),
          stoodDown: stoodDown
        };

        log('reward:', rewardActive ? 'ON' : 'OFF (reward_active = false)',
            '| eligible ' + money(data.eligible_subtotal) + ' / ' + money(data.goal),
            '| qualifies:', qualifies,
            '| gift:', (data.gift_handle || 'none'), 'x' + giftQty,
            '| discount allocated:', money(giftDiscount));

        paint(qualifies);
        watchDrawer();
        tidyLabels(data);
        document.dispatchEvent(new CustomEvent('mk:gift:update', { detail: window.MKGift.state }));

        // ---- gift changed month --------------------------------------------
        // Hand back the previous month's gift first (one unit per pass, the
        // same way any reclaim works). The next check adds this month's gift.
        if (staleGiftLines.length) {
          undiscountedSince = null;
          return reclaimGift(
            staleGiftLines,
            'monthly gift changed to ' + (data.gift_handle || 'none') + ' - reclaimed the previous gift'
          );
        }

        // ---- master switch OFF -------------------------------------------
        // Never add. Hand back only what this script added, and say so once.
        if (!rewardActive) {
          undiscountedSince = null;
          if (ourGiftLines.length) {
            return reclaimGift(
              ourGiftLines,
              'reward system is switched OFF (reward_active = false) - reclaimed the gift this script had added'
            );
          }
          if (!offNoted) {
            offNoted = true;
            log('reward system is switched OFF in cart.reward-data.liquid - standing by, nothing will be added');
          }
          return null;
        }
        offNoted = false;

        if (!qualifies) {
          undiscountedSince = null;
          if (giftQty > 0) {
            return reclaimGift(ourGiftLines, 'reclaimed gift - cart no longer qualifies');
          }
          return null;
        }

        if (giftQty === 0) return addGift();

        if (giftDiscount === 0) {
          if (undiscountedSince === null) {
            undiscountedSince = Date.now();
            log('no discount allocated yet - waiting');
          } else if (Date.now() - undiscountedSince > C.undiscountedGraceMs) {
            warn(
              'Cart qualifies and the gift is present, but Shopify allocated no discount. Check the ' +
              'automatic Buy X Get Y discount is ACTIVE (not scheduled), is AUTOMATIC (not a code), ' +
              'and that its collections match cart.reward-data.liquid. Reclaiming the gift.'
            );
            return reclaimGift(ourGiftLines, 'reclaimed gift - automatic discount did not apply');
          }
          return null;
        }

        undiscountedSince = null;
        return null;
      })
      .catch(function (err) {
        warn(err && err.message ? err.message : 'check failed');
      })
      .then(function () { busy = false; });
  }

  function schedule(delay) {
    clearTimeout(timer);
    timer = setTimeout(check, delay === undefined ? 250 : delay);
  }

  /* ---------- triggers --------------------------------------------------- */

  var CART_WRITE = /\/cart\/(add|change|update|clear)/i;

  try {
    var nativeFetch = window.fetch;
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var out = nativeFetch.apply(this, arguments);
      if (CART_WRITE.test(url)) out.then(function () { schedule(250); }, function () {});
      return out;
    };
  } catch (e) { log('could not wrap fetch', e); }

  try {
    var nativeOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (CART_WRITE.test(String(url))) {
        this.addEventListener('load', function () { schedule(250); });
      }
      return nativeOpen.apply(this, arguments);
    };
  } catch (e) { log('could not wrap XHR', e); }

  var hooked = false;
  function hookUpcart() {
    if (hooked) return true;
    if (typeof window.upcartSubscribeCartLoaded !== 'function') return false;
    hooked = true;
    ['upcartSubscribeCartLoaded',
     'upcartSubscribeCartUpdated',
     'upcartSubscribeAddedToCart',
     'upcartSubscribeItemRemoved',
     'upcartSubscribeCartOpened'].forEach(function (name) {
      try {
        if (typeof window[name] === 'function') window[name](function () { schedule(200); });
      } catch (e) {}
    });
    log('UpCart API hooked');
    return true;
  }

  window.MKGift = {
    config: C,
    state: null,
    check: check,
    data: function () { return lastData; },
    upcartHooked: function () { return hooked; },
    tidy: function () { return tidyLabels(lastData); },
    debugLabels: function () { return tidyLabels(lastData, true); },
    reset: function () {
      writeLog.length = 0;
      stoodDown = false;
      offNoted = false;
      undiscountedSince = null;
      return 'Reset. Run MKGift.check() to re-evaluate.';
    }
  };

  function boot() {
    check();

    if (!hookUpcart()) {
      var tries = 0;
      var waiting = setInterval(function () {
        if (hookUpcart() || ++tries > 60) clearInterval(waiting);
      }, 250);
    }

    // The drawer is often not in the DOM until first opened.
    var watchTries = 0;
    var watching = setInterval(function () {
      if (watchDrawer() || ++watchTries > 120) clearInterval(watching);
    }, 250);

    if (C.pollMs) setInterval(check, C.pollMs);
    window.addEventListener('focus', function () { schedule(300); });
    window.addEventListener('pageshow', function () { schedule(300); });
    document.body.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('[name="add"],[data-add-to-cart],.add-to-cart')) {
        schedule(500);
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();